import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Toast from '../components/Toast';
import EditableEvents from '../components/EditableEvents';
import { useLanguage } from '../contexts/LanguageContext';
import '../styles/admin.css';

function AdminGuestsPage() {
  const navigate = useNavigate();
  const { l } = useLanguage();
  const [groups, setGroups] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [editingGroup, setEditingGroup] = useState(null);
  const [showEventsEditor, setShowEventsEditor] = useState(false);
  const [content, setContent] = useState(null);
  const [editedEvents, setEditedEvents] = useState([]);
  const [formData, setFormData] = useState({
    groupName: '',
    accessCode: '',
    members: [{ name: '', email: '', plusOne: false }],
  });

  useEffect(() => {
    validateTokenAndLoad();
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const response = await fetch('/api/content');
      if (response.ok) {
        const data = await response.json();
        setContent(data);
      }
    } catch (error) {
      console.error('Failed to load content:', error);
    }
  };

  const saveContent = async updatedContent => {
    const token = localStorage.getItem('editToken');
    try {
      const response = await fetch('/api/content', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatedContent),
      });
      if (response.ok) {
        setContent(updatedContent);
        setToast({ message: 'RSVP events updated successfully!', type: 'success' });
      }
    } catch (error) {
      setToast({ message: 'Failed to update RSVP events', type: 'error' });
    }
  };

  const handleSaveEvents = () => {
    saveContent({ ...content, rsvpEvents: editedEvents });
    setShowEventsEditor(false);
  };

  const handleToggleEventsEditor = () => {
    if (!showEventsEditor && content) {
      setEditedEvents(content.rsvpEvents || []);
    }
    setShowEventsEditor(!showEventsEditor);
  };

  const validateTokenAndLoad = async () => {
    const token = localStorage.getItem('editToken');

    if (!token) {
      setToast({ message: 'Please enable edit mode first', type: 'error' });
      setLoadingGroups(false);
      setTimeout(() => navigate(l('/')), 2000);
      return;
    }

    // Token exists, try to load groups (this will validate the token)
    loadGroups();
  };

  const loadGroups = async () => {
    const token = localStorage.getItem('editToken');
    setLoadingGroups(true);

    if (!token) {
      setToast({ message: 'Not authenticated. Please enable edit mode first.', type: 'error' });
      setLoadingGroups(false);
      return;
    }

    try {
      const response = await fetch('/api/admin/guests', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setGroups(data.groups || []);
      } else {
        const errorText = await response.text();
        console.error('Failed to load groups:', response.status, errorText);
        setToast({ message: `Failed to load: ${response.status} - ${errorText}`, type: 'error' });
      }
    } catch (error) {
      console.error('Failed to load guests:', error);
      setToast({ message: `Error: ${error.message}`, type: 'error' });
    } finally {
      setLoadingGroups(false);
    }
  };

  const handleAddMember = () => {
    setFormData({
      ...formData,
      members: [...formData.members, { name: '', email: '', plusOne: false }],
    });
  };

  const handleMemberChange = (index, field, value) => {
    const updated = [...formData.members];
    updated[index][field] = value;
    setFormData({ ...formData, members: updated });
  };

  const handleRemoveMember = index => {
    const updated = formData.members.filter((_, i) => i !== index);
    setFormData({ ...formData, members: updated });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const token = localStorage.getItem('editToken');

    if (!token) {
      setError('Not authenticated. Please enable edit mode first.');
      setLoading(false);
      return;
    }

    try {
      const url = editingGroup ? `/api/admin/guests/${editingGroup.id}` : '/api/admin/guests';
      const method = editingGroup ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        await loadGroups();
        setShowForm(false);
        setEditingGroup(null);
        setFormData({
          groupName: '',
          accessCode: '',
          members: [{ name: '', email: '', plusOne: false }],
        });
        setToast({ message: `Guest group ${editingGroup ? 'updated' : 'created'} successfully!`, type: 'success' });
      } else {
        const data = await response.json();
        setError(data.error || `Failed to ${editingGroup ? 'update' : 'create'} group`);
      }
    } catch (error) {
      console.error(`Failed to ${editingGroup ? 'update' : 'create'} group:`, error);
      setError(`Failed to ${editingGroup ? 'update' : 'create'} group. Check console for details.`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = group => {
    setEditingGroup(group);
    setFormData({
      groupName: group.groupName,
      accessCode: group.accessCode,
      members: group.members.map(m => ({ id: m.id, name: m.name, email: m.email || '', plusOne: m.plusOne || false })),
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setShowForm(false);
    setEditingGroup(null);
    setFormData({
      groupName: '',
      accessCode: '',
      members: [{ name: '', email: '', plusOne: false }],
    });
    setError('');
  };

  const handleDelete = async groupId => {
    if (!window.confirm('Delete this guest group?')) return;

    const token = localStorage.getItem('editToken');
    try {
      const response = await fetch(`/api/admin/guests/${groupId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        await loadGroups();
      }
    } catch (error) {
      console.error('Failed to delete group:', error);
    }
  };

  const copyLink = accessCode => {
    const encodedCode = btoa(accessCode);
    const url = `${window.location.origin}${l('/guest/login')}?code=${encodedCode}`;

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(() => setToast({ message: 'Link copied!', type: 'success' }))
        .catch(() => setToast({ message: 'Failed to copy. Please copy manually.', type: 'error' }));
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setToast({ message: 'Link copied!', type: 'success' });
      } catch (err) {
        setToast({ message: 'Failed to copy. Please copy manually.', type: 'error' });
      }
      document.body.removeChild(textArea);
    }
  };

  const getAttendingCount = group => {
    return group.members.filter(m => m.attending === true).length;
  };

  const toggleExpand = groupId => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  // Calculate Guest Statistics
  const totalBase = groups.reduce((sum, group) => sum + group.members.length, 0);
  const totalPlusOnesAllowed = groups.reduce((sum, group) => sum + group.members.filter(m => m.plusOne).length, 0);
  const totalInvited = totalBase + totalPlusOnesAllowed;

  const baseConfirmed = groups.reduce((sum, group) => sum + group.members.filter(m => m.attending === true).length, 0);
  const plusOnesConfirmed = groups.reduce((sum, group) => sum + group.members.filter(m => m.attending === true && m.bringingPlusOne).length, 0);
  const totalConfirmed = baseConfirmed + plusOnesConfirmed;

  const baseDeclined = groups.reduce((sum, group) => sum + group.members.filter(m => m.attending === false).length, 0);
  const plusOnesDeclined = groups.reduce((sum, group) => {
    return sum + group.members.filter(m => 
      m.plusOne && (m.attending === false || (m.attending === true && !m.bringingPlusOne))
    ).length;
  }, 0);
  const totalDeclined = baseDeclined + plusOnesDeclined;

  const basePending = groups.reduce((sum, group) => sum + group.members.filter(m => m.attending === null || m.attending === undefined).length, 0);
  const plusOnesPending = groups.reduce((sum, group) => sum + group.members.filter(m => m.plusOne && (m.attending === null || m.attending === undefined)).length, 0);
  const totalPending = basePending + plusOnesPending;

  return (
    <div className="admin-guests-page">
      <div className="admin-header">
        <Link to={l('/')} className="back-link">
          ← Back to Home
        </Link>
        <h1>Guest List Manager</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={handleToggleEventsEditor} className="add-btn" style={{ background: '#ff9800' }}>
            {showEventsEditor ? 'Close' : 'Edit RSVP Events'}
          </button>
          <button onClick={() => (showForm ? handleCancelEdit() : setShowForm(true))} className="add-btn">
            {showForm ? 'Cancel' : '+ Add Guest Group'}
          </button>
        </div>
      </div>

      {/* Guest Statistics Dashboard */}
      <div className="admin-stats-grid">
        <div className="admin-stats-card invited">
          <h3>Total Invited</h3>
          <div className="stats-value">{totalInvited}</div>
          <div className="stats-subtext">
            <span>{totalBase} Base</span>
            <span className="separator">•</span>
            <span>{totalPlusOnesAllowed} Plus Ones</span>
          </div>
        </div>
        <div className="admin-stats-card confirmed">
          <h3>Confirmed</h3>
          <div className="stats-value">{totalConfirmed}</div>
          <div className="stats-subtext">
            <span>{baseConfirmed} Base</span>
            <span className="separator">•</span>
            <span>{plusOnesConfirmed} Plus Ones</span>
          </div>
        </div>
        <div className="admin-stats-card declined">
          <h3>Declined</h3>
          <div className="stats-value">{totalDeclined}</div>
          <div className="stats-subtext">
            <span>{baseDeclined} Base</span>
            <span className="separator">•</span>
            <span>{plusOnesDeclined} Plus Ones</span>
          </div>
        </div>
        <div className="admin-stats-card pending">
          <h3>Pending</h3>
          <div className="stats-value">{totalPending}</div>
          <div className="stats-subtext">
            <span>{basePending} Base</span>
            <span className="separator">•</span>
            <span>{plusOnesPending} Plus Ones</span>
          </div>
        </div>
      </div>

      {showEventsEditor && content && (
        <div style={{ background: 'white', padding: '2rem', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', marginBottom: '2rem' }}>
          <EditableEvents events={editedEvents} onChange={setEditedEvents} />
          <button
            onClick={handleSaveEvents}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              marginTop: '1rem',
            }}
          >
            Save Events
          </button>
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="guest-form">
          <h2>{editingGroup ? 'Edit Guest Group' : 'New Guest Group'}</h2>

          <div className="form-group">
            <label>Group Name</label>
            <input
              type="text"
              value={formData.groupName}
              onChange={e => setFormData({ ...formData, groupName: e.target.value })}
              placeholder="e.g., Smith Family"
              required
            />
          </div>

          <div className="form-group">
            <label>Access Code</label>
            <input
              type="text"
              value={formData.accessCode}
              onChange={e => setFormData({ ...formData, accessCode: e.target.value })}
              placeholder="e.g., smith2026"
              required
            />
          </div>

          <div className="form-group">
            <label>Members</label>
            {formData.members.map((member, index) => (
              <div key={index} className="member-input">
                <input type="text" value={member.name} onChange={e => handleMemberChange(index, 'name', e.target.value)} placeholder="Name" required />
                <input type="email" value={member.email} onChange={e => handleMemberChange(index, 'email', e.target.value)} placeholder="Email (optional)" />
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input type="checkbox" checked={member.plusOne} onChange={e => handleMemberChange(index, 'plusOne', e.target.checked)} />
                  <span>Plus One</span>
                </label>
                {formData.members.length > 1 && (
                  <button type="button" onClick={() => handleRemoveMember(index)}>
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button type="button" onClick={handleAddMember} className="add-member-btn">
              + Add Member
            </button>
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? (editingGroup ? 'Updating...' : 'Creating...') : editingGroup ? 'Update Group' : 'Create Group'}
          </button>
        </form>
      )}

      <div className="guests-list">
        <h2>Guest Groups ({groups.length})</h2>
        {loadingGroups ? (
          <p>Loading guest groups...</p>
        ) : groups.length === 0 ? (
          <p>No guest groups yet</p>
        ) : (
          <table className="guests-table">
            <thead>
              <tr>
                <th></th>
                <th>Group Name</th>
                <th>Members</th>
                <th>Attending</th>
                <th>RSVP Status</th>
                <th>Access Code</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(group => (
                <React.Fragment key={group.id}>
                  <tr>
                    <td data-label="">
                      <button
                        onClick={() => toggleExpand(group.id)}
                        className="expand-btn"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem' }}
                      >
                        {expandedGroups.has(group.id) ? '▼' : '▶'}
                      </button>
                    </td>
                    <td data-label="Group Name">{group.groupName}</td>
                    <td data-label="Members">{group.members.length}</td>
                    <td data-label="Attending">{getAttendingCount(group)}</td>
                    <td data-label="RSVP Status">
                      {group.rsvpSubmitted ? (
                        <span className="status-submitted">✓ {new Date(group.rsvpSubmittedAt).toLocaleDateString()}</span>
                      ) : (
                        <span className="status-pending">Pending</span>
                      )}
                    </td>
                    <td data-label="Access Code">
                      <code>{group.accessCode}</code>
                    </td>
                    <td data-label="Actions">
                      <button onClick={() => copyLink(group.accessCode)} className="copy-btn">
                        Copy Link
                      </button>
                      <Link to={l(`/gallery/${group.albumId}`)} className="view-album-btn">
                        View Album
                      </Link>
                      <button onClick={() => handleEdit(group)} className="edit-btn">
                        Edit
                      </button>
                      <button onClick={() => handleDelete(group.id)} className="delete-btn">
                        Delete
                      </button>
                    </td>
                  </tr>
                  {expandedGroups.has(group.id) && (
                    <tr className="expanded-row">
                      <td colSpan="7">
                        <div className="member-details">
                          {(group.firstLogin || group.lastLogin) && (
                            <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f0f7f0', borderRadius: '8px' }}>
                              <h4 style={{ color: 'var(--color-primary)', marginBottom: '0.5rem' }}>Login Activity</h4>
                              {group.firstLogin && (
                                <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>
                                  <strong>First Login:</strong> {new Date(group.firstLogin).toLocaleString()}
                                </p>
                              )}
                              {group.lastLogin && (
                                <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>
                                  <strong>Last Login:</strong> {new Date(group.lastLogin).toLocaleString()}
                                </p>
                              )}
                            </div>
                          )}
                          <h3>Member Details</h3>
                          {group.members.map((member, idx) => (
                            <div key={idx} className="member-detail-card">
                              <h4>{member.name}</h4>
                              {member.email && (
                                <p>
                                  <strong>Email:</strong> {member.email}
                                </p>
                              )}
                              <p>
                                <strong>Attending:</strong> {member.attending === true ? '✓ Yes' : member.attending === false ? '✗ No' : 'Not answered'}
                              </p>
                              {member.attending && member.events && content && (
                                <p>
                                  <strong>Events:</strong>{' '}
                                  {(content.rsvpEvents || [])
                                    .filter(event => member.events[event.id])
                                    .map(event => event.label)
                                    .join(', ') || 'None selected'}
                                </p>
                              )}
                              {member.attending && member.accomodation && content && (
                                <p>
                                  <strong>Accomodation:</strong>{' '}
                                  {(content.rsvpAccomodationOptions || [])
                                    .filter(accomOption => member.accomodation[accomOption.id])
                                    .map(accomOption => accomOption.label)
                                    .join(', ') || 'None selected'}
                                </p>
                              )}
                              {member.dietaryRequirements && (
                                <p>
                                  <strong>Dietary:</strong> {member.dietaryRequirements}
                                </p>
                              )}
                              {member.extraNotes && (
                                <p>
                                  <strong>Notes:</strong> {member.extraNotes}
                                </p>
                              )}
                              {member.plusOne && (
                                <p>
                                  <strong>Plus One:</strong> {member.bringingPlusOne ? '✓ Bringing' : 'Not bringing'}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

export default AdminGuestsPage;
